<script lang="ts">
  import { showAlert } from '../lib/ui-alert';
  import { onMount } from 'svelte';
  import { push } from 'svelte-spa-router';
  import AppShell from '../components/AppShell.svelte';
  import Icon from '../components/Icon.svelte';
  import {
    listAutomations,
    createAutomation,
    deleteAutomation,
    updateAutomation,
    listCredentials,
    createCredential,
    updateCredential,
    deleteCredential,
    type AutomationListItem,
    type CredentialItem,
    type CredentialKind,
  } from '../lib/automations-api';
  import { fmtDateTime } from '../lib/format';

  let tab = $state<'automations' | 'credentials'>('automations');

  let items = $state<AutomationListItem[]>([]);
  let loading = $state(true);
  let err = $state<string | null>(null);

  let createOpen = $state(false);
  let createBusy = $state(false);
  let createName = $state('');
  let createErr = $state<string | null>(null);

  // ── Доступи до сервісів (credentials) ───────────────────────────────────────
  /** Конфіг видів: лейбл + структуровані поля секрету (generic = JSON). */
  const CRED_KINDS: {
    value: CredentialKind;
    label: string;
    fields: { key: string; label: string; password?: boolean }[];
  }[] = [
    { value: 'bearer', label: 'Bearer token', fields: [{ key: 'token', label: 'Token', password: true }] },
    { value: 'basic', label: 'Basic (логін/пароль)', fields: [{ key: 'username', label: 'Логін' }, { key: 'password', label: 'Пароль', password: true }] },
    { value: 'header', label: 'Header (ключ у заголовку)', fields: [{ key: 'headerName', label: 'Назва заголовка' }, { key: 'headerValue', label: 'Значення', password: true }] },
    { value: 'apiKeyQuery', label: 'API key (query-параметр)', fields: [{ key: 'queryName', label: 'Назва параметра' }, { key: 'queryValue', label: 'Значення', password: true }] },
    { value: 'generic', label: 'Generic (довільні заголовки)', fields: [] },
  ];
  function kindLabel(k: CredentialKind): string {
    return CRED_KINDS.find((c) => c.value === k)?.label ?? k;
  }

  let creds = $state<CredentialItem[]>([]);
  let credLoading = $state(false);
  let credErr = $state<string | null>(null);

  let credModalOpen = $state(false);
  let credBusy = $state(false);
  let credModalErr = $state<string | null>(null);
  let credEditId = $state<string | null>(null); // null → створення
  let credName = $state('');
  let credKind = $state<CredentialKind>('bearer');
  let credFields = $state<Record<string, string>>({}); // значення структурованих полів
  let credGenericJson = $state('{\n  "headers": { "X-Foo": "bar" }\n}');
  const credKindCfg = $derived(CRED_KINDS.find((c) => c.value === credKind));

  async function loadCreds() {
    credLoading = true;
    credErr = null;
    try {
      creds = await listCredentials();
    } catch (e) {
      credErr = e instanceof Error ? e.message : String(e);
    } finally {
      credLoading = false;
    }
  }
  $effect(() => {
    if (tab === 'credentials') void loadCreds();
  });

  function openCreateCred() {
    credEditId = null;
    credName = '';
    credKind = 'bearer';
    credFields = {};
    credGenericJson = '{\n  "headers": { "X-Foo": "bar" }\n}';
    credModalErr = null;
    credModalOpen = true;
  }
  function openEditCred(c: CredentialItem) {
    credEditId = c.id;
    credName = c.name;
    credKind = c.kind;
    credFields = {}; // секрети не повертаються — лишаємо порожнім (= не змінювати)
    credGenericJson = '';
    credModalErr = null;
    credModalOpen = true;
  }

  /** Чи введено хоч одне поле секрету. */
  function credHasSecret(): boolean {
    if (credKind === 'generic') return credGenericJson.trim() !== '';
    return (credKindCfg?.fields ?? []).some((f) => (credFields[f.key] ?? '').trim() !== '');
  }
  function buildCredData(): Record<string, unknown> {
    if (credKind === 'generic') return JSON.parse(credGenericJson || '{}') as Record<string, unknown>;
    const d: Record<string, unknown> = {};
    for (const f of credKindCfg?.fields ?? []) {
      const v = (credFields[f.key] ?? '').trim();
      if (v) d[f.key] = v;
    }
    return d;
  }

  async function submitCred(e: SubmitEvent) {
    e.preventDefault();
    if (!credName.trim()) {
      credModalErr = 'Введіть назву';
      return;
    }
    let data: Record<string, unknown> | null = null;
    if (credHasSecret()) {
      try {
        data = buildCredData();
      } catch {
        credModalErr = 'Generic: секрет має бути валідним JSON';
        return;
      }
    }
    credBusy = true;
    credModalErr = null;
    try {
      if (credEditId) {
        await updateCredential(credEditId, { name: credName.trim(), kind: credKind, ...(data ? { data } : {}) });
      } else {
        await createCredential(credName.trim(), credKind, data ?? {});
      }
      credModalOpen = false;
      await loadCreds();
    } catch (e) {
      credModalErr = e instanceof Error ? e.message : String(e);
    } finally {
      credBusy = false;
    }
  }

  async function removeCred(c: CredentialItem) {
    if (!confirm(`Видалити доступ "${c.name}"? Автоматизації, що ним користуються, втратять авторизацію.`)) return;
    try {
      await deleteCredential(c.id);
      await loadCreds();
    } catch (e) {
      showAlert(e instanceof Error ? e.message : String(e));
    }
  }

  async function load() {
    loading = true;
    err = null;
    try {
      items = await listAutomations();
    } catch (e) {
      err = e instanceof Error ? e.message : String(e);
    } finally {
      loading = false;
    }
  }

  onMount(() => {
    void load();
  });

  function openCreate() {
    createName = 'Нова автоматизація';
    createErr = null;
    createOpen = true;
  }

  async function submitCreate(e: SubmitEvent) {
    e.preventDefault();
    if (!createName.trim()) {
      createErr = 'Введіть назву';
      return;
    }
    createBusy = true;
    createErr = null;
    try {
      const id = await createAutomation(createName.trim());
      createOpen = false;
      push(`/automations/${id}`);
    } catch (e) {
      createErr = e instanceof Error ? e.message : String(e);
    } finally {
      createBusy = false;
    }
  }

  async function toggleActive(item: AutomationListItem) {
    try {
      await updateAutomation(item.id, { isActive: !item.isActive });
      await load();
    } catch (e) {
      showAlert(e instanceof Error ? e.message : String(e));
    }
  }

  async function remove(item: AutomationListItem) {
    if (!confirm(`Видалити автоматизацію "${item.name}"?`)) return;
    try {
      await deleteAutomation(item.id);
      await load();
    } catch (e) {
      showAlert(e instanceof Error ? e.message : String(e));
    }
  }

  function statusBadgeClass(status: string | null): string {
    if (status === 'success') return 'bg-emerald-100 text-emerald-700';
    if (status === 'failed') return 'bg-rose-100 text-rose-700';
    if (status === 'skipped') return 'bg-amber-100 text-amber-700';
    return 'bg-slate-100 text-slate-500';
  }
</script>

<AppShell active="automations">
  <div class="mb-5">
    <div class="flex items-center justify-between">
      <div>
        <h1 class="inline-flex items-center gap-2 text-xl font-semibold"><Icon name="automations" size={20} /> Автоматизації</h1>
        <p class="text-xs text-[var(--color-text-muted)] mt-1">
          {tab === 'automations'
            ? 'Webhook/розклад/подія → дії: Person, Payment, записи, HTTP.'
            : 'Збережені доступи до сервісів (токени, ключі) — обираються в нодах автоматизацій.'}
        </p>
      </div>
      {#if tab === 'automations'}
        <button
          class="rounded-lg px-4 py-2 text-xs font-semibold text-white"
          style="background: linear-gradient(135deg, var(--color-pu), var(--color-pu-2));"
          onclick={openCreate}
        >+ Нова автоматизація</button>
      {:else}
        <button
          class="rounded-lg px-4 py-2 text-xs font-semibold text-white"
          style="background: linear-gradient(135deg, var(--color-pu), var(--color-pu-2));"
          onclick={openCreateCred}
        >+ Новий доступ</button>
      {/if}
    </div>
    <!-- Вкладки -->
    <div class="flex gap-2 border-b border-[var(--color-border-3)] mt-4">
      <button
        class="px-4 py-2 text-xs font-semibold border-b-2 -mb-[1px] {tab === 'automations' ? 'border-[var(--color-pu)] text-[var(--color-pu-3)]' : 'border-transparent text-[var(--color-text-muted)]'}"
        onclick={() => (tab = 'automations')}
      ><span class="inline-flex items-center gap-1.5"><Icon name="automations" size={13} /> Автоматизації</span></button>
      <button
        class="px-4 py-2 text-xs font-semibold border-b-2 -mb-[1px] {tab === 'credentials' ? 'border-[var(--color-pu)] text-[var(--color-pu-3)]' : 'border-transparent text-[var(--color-text-muted)]'}"
        onclick={() => (tab = 'credentials')}
      ><span class="inline-flex items-center gap-1.5"><Icon name="lock" size={13} /> Доступи до сервісів</span></button>
    </div>
  </div>

  {#if tab === 'credentials'}
    {#if credLoading}
      <div class="text-sm text-[var(--color-text-muted)]">Завантаження…</div>
    {:else if credErr}
      <div class="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{credErr}</div>
    {:else if creds.length === 0}
      <div class="rounded-2xl border border-dashed border-[var(--color-border-3)] bg-white p-10 text-center">
        <div class="mb-2 inline-flex text-[var(--color-text-muted)]"><Icon name="lock" size={30} /></div>
        <div class="text-sm font-semibold mb-1">Ще немає збережених доступів</div>
        <div class="text-xs text-[var(--color-text-muted)] mb-4">
          Додайте токен/ключ сервісу — і обирайте його в HTTP-нодах автоматизацій.
        </div>
        <button
          class="rounded-lg px-4 py-2 text-xs font-semibold text-white"
          style="background: linear-gradient(135deg, var(--color-pu), var(--color-pu-2));"
          onclick={openCreateCred}
        >+ Додати доступ</button>
      </div>
    {:else}
      <div class="rounded-2xl border border-[var(--color-border-3)] bg-white overflow-hidden">
        <table class="w-full text-sm">
          <thead class="bg-[var(--color-bg-3)] text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">
            <tr>
              <th class="text-left px-4 py-2.5 font-semibold">Назва</th>
              <th class="text-left px-4 py-2.5 font-semibold">Тип</th>
              <th class="text-right px-4 py-2.5 font-semibold">Оновлено</th>
              <th class="w-40"></th>
            </tr>
          </thead>
          <tbody>
            {#each creds as c (c.id)}
              <tr class="border-t border-[var(--color-border-3)] hover:bg-[var(--color-bg-3)]">
                <td class="px-4 py-3">
                  <div class="inline-flex items-center gap-2 font-semibold"><Icon name="lock" size={14} /> {c.name}</div>
                </td>
                <td class="px-4 py-3">
                  <span class="rounded-full bg-purple-50 text-[var(--color-pu-3)] px-2.5 py-0.5 text-[10px] font-semibold">{kindLabel(c.kind)}</span>
                </td>
                <td class="px-4 py-3 text-right text-xs text-[var(--color-text-muted)]">{fmtDateTime(c.updatedAt)}</td>
                <td class="px-4 py-3 text-right">
                  <button class="text-xs text-[var(--color-pu-3)] hover:underline mr-3" onclick={() => openEditCred(c)}>Редагувати</button>
                  <button class="text-xs text-[var(--color-text-muted)] hover:text-rose-600" onclick={() => removeCred(c)}>Видалити</button>
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {/if}
  {:else if loading}
    <div class="text-sm text-[var(--color-text-muted)]">Завантаження…</div>
  {:else if err}
    <div class="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{err}</div>
  {:else if items.length === 0}
    <div class="rounded-2xl border border-dashed border-[var(--color-border-3)] bg-white p-10 text-center">
      <div class="mb-2 inline-flex text-[var(--color-text-muted)]"><Icon name="automations" size={30} /></div>
      <div class="text-sm font-semibold mb-1">Поки немає жодної автоматизації</div>
      <div class="text-xs text-[var(--color-text-muted)] mb-4">
        Створіть першу — отримаєте webhook URL для зовнішніх систем.
      </div>
      <button
        class="rounded-lg px-4 py-2 text-xs font-semibold text-white"
        style="background: linear-gradient(135deg, var(--color-pu), var(--color-pu-2));"
        onclick={openCreate}
      >+ Створити автоматизацію</button>
    </div>
  {:else}
    <div class="rounded-2xl border border-[var(--color-border-3)] bg-white overflow-hidden">
      <table class="w-full text-sm">
        <thead class="bg-[var(--color-bg-3)] text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">
          <tr>
            <th class="text-left px-4 py-2.5 font-semibold">Назва</th>
            <th class="text-left px-4 py-2.5 font-semibold">Статус</th>
            <th class="text-left px-4 py-2.5 font-semibold">Останній запуск</th>
            <th class="text-right px-4 py-2.5 font-semibold">Запусків</th>
            <th class="text-right px-4 py-2.5 font-semibold">Помилок</th>
            <th class="text-right px-4 py-2.5 font-semibold">Оновлено</th>
            <th class="w-32"></th>
          </tr>
        </thead>
        <tbody>
          {#each items as a (a.id)}
            <tr class="border-t border-[var(--color-border-3)] hover:bg-[var(--color-bg-3)]">
              <td class="px-4 py-3">
                <button class="text-left hover:underline" onclick={() => push(`/automations/${a.id}`)}>
                  <div class="font-semibold">{a.name}</div>
                  <div class="text-[10px] text-[var(--color-text-muted)] font-mono mt-0.5">
                    /api/automations/hooks/{a.webhookToken.slice(0, 8)}…
                  </div>
                </button>
              </td>
              <td class="px-4 py-3">
                <button
                  class="rounded-full px-2.5 py-0.5 text-[10px] font-bold {a.isActive
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-slate-100 text-slate-500'}"
                  onclick={() => toggleActive(a)}
                >{a.isActive ? '● Активно' : '○ Неактивно'}</button>
              </td>
              <td class="px-4 py-3 text-xs">
                {#if a.lastRunAt}
                  <div class="flex items-center gap-2">
                    <span class="rounded px-2 py-0.5 text-[10px] {statusBadgeClass(a.lastRunStatus)}">
                      {a.lastRunStatus ?? '—'}
                    </span>
                    <span class="text-[var(--color-text-muted)]">{fmtDateTime(a.lastRunAt)}</span>
                  </div>
                {:else}
                  <span class="text-[var(--color-text-muted)]">—</span>
                {/if}
              </td>
              <td class="px-4 py-3 text-right text-xs tabular-nums">{a.runCount}</td>
              <td class="px-4 py-3 text-right text-xs tabular-nums {a.failureCount > 0 ? 'text-rose-600 font-semibold' : ''}">{a.failureCount}</td>
              <td class="px-4 py-3 text-right text-xs text-[var(--color-text-muted)]">{fmtDateTime(a.updatedAt)}</td>
              <td class="px-4 py-3 text-right">
                <button
                  class="text-xs text-[var(--color-text-muted)] hover:text-rose-600"
                  onclick={() => remove(a)}
                  title="Видалити"
                >Видалити</button>
              </td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
  {/if}

  {#if createOpen}
    <div
      class="fixed inset-0 bg-black/40 grid place-items-center z-50"
      onclick={() => (createOpen = false)}
      onkeydown={(e) => e.key === 'Escape' && (createOpen = false)}
      role="presentation"
    >
      <form
        onsubmit={submitCreate}
        onclick={(e) => e.stopPropagation()}
        class="bg-white rounded-2xl p-6 w-[420px] shadow-2xl"
        role="dialog"
      >
        <div class="text-base font-semibold mb-4">Створити нову автоматизацію</div>
        <label class="block text-xs text-[var(--color-text-muted)] mb-1">Назва автоматизації</label>
        <input
          bind:value={createName}
          autofocus
          class="w-full rounded-lg border border-[var(--color-border-3)] px-3 py-2 text-sm"
        />
        {#if createErr}
          <div class="mt-2 text-xs text-rose-600">{createErr}</div>
        {/if}
        <div class="flex justify-end gap-2 mt-5">
          <button
            type="button"
            class="rounded-lg px-3 py-1.5 text-xs font-medium text-[var(--color-text-muted)]"
            onclick={() => (createOpen = false)}
          >Скасувати</button>
          <button
            type="submit"
            disabled={createBusy}
            class="rounded-lg px-4 py-1.5 text-xs font-semibold text-white"
            style="background: linear-gradient(135deg, var(--color-pu), var(--color-pu-2));"
          >{createBusy ? 'Створення…' : 'Продовжити'}</button>
        </div>
      </form>
    </div>
  {/if}

  {#if credModalOpen}
    <div
      class="fixed inset-0 bg-black/40 grid place-items-center z-50 p-6"
      onclick={() => (credModalOpen = false)}
      onkeydown={(e) => e.key === 'Escape' && (credModalOpen = false)}
      role="presentation"
    >
      <form
        onsubmit={submitCred}
        onclick={(e) => e.stopPropagation()}
        class="bg-white rounded-2xl p-6 w-[460px] max-h-[85vh] overflow-y-auto shadow-2xl"
        role="dialog"
      >
        <div class="text-base font-semibold mb-4">{credEditId ? 'Редагувати доступ' : 'Новий доступ до сервісу'}</div>

        <label class="block text-xs text-[var(--color-text-muted)] mb-1">Назва</label>
        <input
          bind:value={credName}
          placeholder="напр. KeyCRM API"
          class="w-full rounded-lg border border-[var(--color-border-3)] px-3 py-2 text-sm"
        />

        <label class="block text-xs text-[var(--color-text-muted)] mb-1 mt-3">Тип</label>
        <select bind:value={credKind} class="w-full rounded-lg border border-[var(--color-border-3)] px-3 py-2 text-sm">
          {#each CRED_KINDS as k (k.value)}<option value={k.value}>{k.label}</option>{/each}
        </select>

        {#if credEditId}
          <div class="mt-2 rounded-lg bg-[var(--color-bg-3)] px-3 py-2 text-[11px] text-[var(--color-text-muted)]">
            Секрет не показується. Лишіть поля порожніми — він не зміниться. Зміна типу потребує повторного введення секрету.
          </div>
        {/if}

        <div class="mt-3 space-y-2">
          {#if credKind === 'generic'}
            <label class="block text-xs text-[var(--color-text-muted)]">Дані (JSON)</label>
            <textarea
              bind:value={credGenericJson}
              rows="4"
              placeholder={'{ "headers": { "X-Foo": "bar" } }'}
              class="w-full rounded-lg border border-[var(--color-border-3)] px-3 py-2 text-[12px] font-mono resize-y"
            ></textarea>
          {:else}
            {#each credKindCfg?.fields ?? [] as f (f.key)}
              <label class="block text-xs text-[var(--color-text-muted)]">{f.label}</label>
              <input
                type={f.password ? 'password' : 'text'}
                bind:value={credFields[f.key]}
                placeholder={credEditId ? '— не змінювати —' : ''}
                class="w-full rounded-lg border border-[var(--color-border-3)] px-3 py-2 text-sm"
              />
            {/each}
          {/if}
        </div>

        {#if credModalErr}<div class="mt-2 text-xs text-rose-600">{credModalErr}</div>{/if}

        <div class="flex justify-end gap-2 mt-5">
          <button
            type="button"
            class="rounded-lg px-3 py-1.5 text-xs font-medium text-[var(--color-text-muted)]"
            onclick={() => (credModalOpen = false)}
          >Скасувати</button>
          <button
            type="submit"
            disabled={credBusy}
            class="rounded-lg px-4 py-1.5 text-xs font-semibold text-white"
            style="background: linear-gradient(135deg, var(--color-pu), var(--color-pu-2));"
          >{credBusy ? 'Збереження…' : credEditId ? 'Зберегти' : 'Додати'}</button>
        </div>
      </form>
    </div>
  {/if}
</AppShell>
